import { subscribeKey } from "valtio/utils";

import {
  __ONLY_WRITE_STATE_FROM_ACTIONS as writeState,
  readState,
} from "@/__main__/app-state.mjs";
import { IS_APP, IS_DEV, IS_TESTING } from "@/__main__/constants.mjs";
import blitzMessage, { EVENTS, handleMessage } from "@/__main__/ipc-core.mjs";
import mainRefs from "@/__main__/refs.mjs";
import router from "@/__main__/router.mjs";
import { accountRefs } from "@/app/account.refs.mjs";
import appRefs from "@/app/refs.mjs";
import adsRefs from "@/feature-ads/refs.mjs";
import AccountMenuSignInOutButton from "@/feature-auth/components/AccountMenuSignInOutButton.jsx";
import DeleteAccountModal from "@/feature-auth/components/DeleteAccountModal.jsx";
import EditEmailModal from "@/feature-auth/components/EditEmailModal.jsx";
import EditSettingsButton from "@/feature-auth/components/EditSettingsButton.jsx";
import EditUserSettingsModal from "@/feature-auth/components/EditUserSettingsModal.jsx";
import SignInButton from "@/feature-auth/components/SignInButton.jsx";
import { removeActiveSessionData } from "@/feature-auth/fetches/remove-active-session.mjs";
import type { User } from "@/feature-auth/models/user-model.mjs";
import {
  getActiveSessionsData,
  initAuth,
  resetInitAuth,
  setUserAction,
} from "@/feature-auth/utils/auth-actions.mjs";
import { authTokenHandler } from "@/feature-auth/utils/auth-token-handler.mjs";
import { checkForAuthorization } from "@/feature-auth/utils/check-authorization.mjs";
import getBearerToken from "@/feature-auth/utils/get-auth-request-header.mjs";
import {
  registerUserRolesSubscription,
  setAdsFeatureFromUserRoles,
  unregisterUserRolesSubscription,
} from "@/feature-auth/utils/user-roles-change-listener.mjs";
import {
  applyUserSettings,
  unsubscribeSettings,
} from "@/feature-auth/utils/user-settings.mjs";
import { isUserSubscriber } from "@/feature-subscriber/utils/subscriber.mjs";
import lolClientApiRefs from "@/game-lol/utils/lol-client-api.refs.mjs";
import { valorantRefs } from "@/game-val/refs.mjs";
import settingsRefs from "@/hub-settings/refs.mjs";
import generalRefs from "@/routes/general.refs.mjs";
import type { BaseRoute } from "@/routes/routes.mjs";
import routes, { appRoutes } from "@/routes/routes.mjs";
import addInitialPath from "@/util/add-initial-path.mjs";
import globals from "@/util/global-whitelist.mjs";
import lazyFn from "@/util/lazy-fn.mjs";
import mapOriginalRefs from "@/util/map-original-refs.mjs";
import pathRegExp from "@/util/path-regexp.mjs";
import subscriptionStateDiff from "@/util/subscription-state-diff.mjs";
import { fetchCountryCode } from "@/util/use-country-code.mjs";

export const authRoutes: BaseRoute[] = [
  {
    path: "/account",
    component: "feature-auth/Authentication.tsx",
    fetchData: () => fetchCountryCode(),
  },
  !IS_APP &&
    (IS_DEV || IS_TESTING) && {
      path: pathRegExp(
        "/:tab1(auth|email)/:tab2(verify|new|old|invalid|valid)",
      ),
      component: "feature-auth/AuthResult.tsx",
    },
].filter(Boolean);

const original = mapOriginalRefs({
  adRefs: adsRefs,
  appRefs,
  appRefsAccountMenuItems: appRefs.accountMenuItems,
  settingsRefs,
  generalRefs,
  valorantRefs,
  lolClientApiRefs,
  mainRefs,
});

let unsubscribeAccountRef: (() => void) | null = null;
let unsubscribeUser: (() => void) | null = null;

// REFACTOR: this function is getting long, and some of the code can be
// simplified as data. Especially need setup/teardown tuples to guarantee
// that we're not missing teardown.
export function setup() {
  addInitialPath(["auth"], {
    token: undefined,
  });

  original.set({
    appRefs: {
      SignIn: SignInButton,
      getBearerToken,
    },

    appRefsAccountMenuItems: {
      footerSlot: AccountMenuSignInOutButton,
    },

    settingsRefs: {
      Edit: EditSettingsButton,
      UserSettings: EditUserSettingsModal,
      DeleteUser: DeleteAccountModal,
      EditEmail: EditEmailModal,
      removeSession: removeActiveSessionData,
    },

    generalRefs: {
      getActiveSessionsData,
      fetchUserProInfo: lazyFn(
        () => import("@/feature-subscriber/fetch-user-pro-info.mjs"),
      ),
    },

    lolClientApiRefs: {
      shouldFocusOnPostMatch: () => {
        return !isUserSubscriber(readState.user);
      },
    },

    mainRefs: {
      checkForAuthorization,
    },
  });

  original.append({
    adRefs: {
      shouldShowAds: [
        () =>
          !authRoutes.some(({ path }) => {
            return path === router.route?.path;
          }),
      ],
    },
  });

  appRoutes.push(...authRoutes);
  routes.push(...authRoutes);

  unsubscribeAccountRef = subscribeKey(readState, "user", updateAccountRef);

  handleMessage(EVENTS.REQUEST_USER_ROLES, () => {
    blitzMessage(EVENTS.USER_ROLES, readState.user?.roles);
  });

  initAuth().then(() => {
    if (readState.user) {
      applyUserSettings(readState.user);
    }

    unsubscribeUser = subscribeKey(
      readState,
      "user",
      subscriptionStateDiff(applyUserSettings, readState.user),
    );

    // This is called earlier, but apparently it needs to be called again!
    setAdsFeatureFromUserRoles();

    registerUserRolesSubscription();
  });

  globals.__BLITZ_DEV__.auth = authTokenHandler;
}

export async function teardown() {
  // Ensure auth has fully initialized before tearing down
  await initAuth();

  delete writeState.auth;

  original.restore();

  accountRefs.isLoggedIn = false;

  unsubscribeAccountRef?.();
  unsubscribeUser?.();
  unsubscribeSettings?.();

  for (const authRoute of authRoutes) {
    appRoutes.splice(appRoutes.indexOf(authRoute), 1);
    routes.splice(routes.indexOf(authRoute), 1);
  }

  setUserAction(null);
  resetInitAuth();

  unregisterUserRolesSubscription();
  delete globals.__BLITZ_DEV__.auth;
}

function updateAccountRef(user: User) {
  accountRefs.isLoggedIn = user !== undefined;
}

// Exposed for testing
export const authClientLoader = async () =>
  (await import("@/feature-auth/utils/auth-client.mjs")).AuthClient;
export { authTokenHandler };

globals.__BLITZ_DEV__.authClientLoader = authClientLoader;
